import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

interface PhaseConfig {
  number: number;
  colorVar: string;
  icon: string;
}

@Component({
  selector: 'app-page-transition',
  templateUrl: './page-transition.component.html',
  styleUrls: ['./page-transition.component.scss']
})
export class PageTransitionComponent implements OnInit, OnDestroy {
  
  currentPhase: PhaseConfig | null = null;
  isTransitioning: boolean = false;
  private destroy$ = new Subject<void>();
  private hasInitialized: boolean = false;
  
  private phaseConfigs: { [key: string]: PhaseConfig } = {
    'phase-one': {
      number: 1,
      colorVar: '--phase1MainColor',
      icon: 'assets/icons/cuaderno-persona-white.svg',
    },
    'phase-two': {
      number: 2,
      colorVar: '--phase2MainColor',
      icon: 'assets/icons/flecha-white.svg',
    },
    'phase-three': {
      number: 3,
      colorVar: '--phase3MainColor',
      icon: 'assets/icons/rayado-lapiz-white.svg',
    },
    'phase-four': {
      number: 4,
      colorVar: '--phase4MainColor',
      icon: 'assets/icons/monitor-figuras-white.svg',
    },
    'phase-five': {
      number: 5,
      colorVar: '--phase5MainColor',
      icon: 'assets/icons/check-paper-white.svg',
    },
    'phase-six': {
      number: 6,
      colorVar: '--phase6MainColor',
      icon: 'assets/icons/monitor-ojo-white.svg',
    },
    'phase-seven': {
      number: 7,
      colorVar: '--phase7MainColor',
      icon: 'assets/icons/persona-white.svg',
    }
  };

  constructor(private router: Router) { }

  ngOnInit(): void {
    // Arranque: no mostrar overlay aunque la URL sea una fase
    // Suscribirse a inicios de navegación para activar transición solo hacia rutas de fase
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        takeUntil(this.destroy$)
      )
      .subscribe((event: any) => {
        const nextUrl: string = event.url || '';
        const phaseKey = this.extractPhaseKey(nextUrl);
        if (phaseKey) {
          this.currentPhase = this.phaseConfigs[phaseKey];
          this.isTransitioning = true;
        }
      });

    // Suscribirse a fin/cancelación de navegación para ocultar el overlay tras breve delay
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd || event instanceof NavigationCancel),
        takeUntil(this.destroy$)
      )
      .subscribe((event: any) => {
        // Actualizar fase según URL final, pero no activar transición en carga inicial
        if (!this.hasInitialized && event instanceof NavigationEnd) {
          this.updatePhaseConfig(event.url);
          this.isTransitioning = false; // explícito en carga
          this.hasInitialized = true;
          return;
        }

        const finalUrl: string = event.url || this.router.url || '';
        this.updatePhaseConfig(finalUrl);
        // Dar tiempo a la animación antes de ocultar
        setTimeout(() => {
          this.isTransitioning = false;
        }, 600);
      });
  }

  private updatePhaseConfig(url: string): void {
    const phaseKey = this.extractPhaseKey(url);
    this.currentPhase = phaseKey ? this.phaseConfigs[phaseKey] : null;
  }

  private extractPhaseKey(url: string): string | null {
    const routeSegments = (url || '').split('/');
    const phaseRoute = routeSegments.find(segment => segment.startsWith('phase-')) || null;
    return phaseRoute && this.phaseConfigs[phaseRoute] ? phaseRoute : null;
  }

  getCurrentPhaseColor(): string {
    return this.currentPhase ? `var(${this.currentPhase.colorVar})` : 'var(--mainColor)';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}